import { TagsQuerySelect } from "org/tags/queries/useTagsQuery";
import { RowError, OperationResult } from "utils/spreadsheet/types";

import { AssignmentRow, FIRST_DATA_ROW } from "./spreadsheetParser";

/**
 * Context required for validating control assignments
 * @property tagsData - Available tag categories and tags
 * @property existingControlIds - Set of valid control IDs
 * @property existingUserIds - Set of valid user IDs
 * @property existingUserGroupIds - Set of valid user group IDs
 * @property existingUsersWithinGroupIds - Map of user group IDs to sets of user IDs, representing users within each group
 * @property controlProgramIdByControlId - Map of control IDs to the ID of the control program they belong to
 * @property entityIdsByControlProgramId - Map of control program IDs to sets of IDs of the entities linked to them
 */
export interface ValidationContext {
    tagsData: TagsQuerySelect;
    existingControlIds: Set<string>;
    existingUserIds: Set<number>;
    existingUserGroupIds: Set<number>;
    existingUsersWithinGroupIds: Map<number, Set<number>>;
    controlProgramIdByControlId: Map<string, number>;
    entityIdsByControlProgramId: Map<number, Set<number>>;
}

type Validator = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
) => RowError[];

const validateControl = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
): RowError[] => {
    if (!context.existingControlIds.has(row.Control_ID)) {
        return [
            {
                row: index + FIRST_DATA_ROW,
                field: "Control_ID",
                message: `Control with ID ${row.Control_ID} does not exist`,
            },
        ];
    }
    return [];
};

const validateUsers = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
): RowError[] => {
    const userFields: Array<{ id: number | undefined; field: string }> = [
        { id: row.Responsible_ID, field: "Responsible_ID" },
        { id: row.Reviewer_ID, field: "Reviewer_ID" },
        { id: row.SecondReviewer_ID, field: "SecondReviewer_ID" },
    ];

    return userFields
        .filter((entry) => entry.id !== undefined)
        .filter((entry) => !context.existingUserIds.has(entry.id!))
        .map((entry) => ({
            row: index + FIRST_DATA_ROW,
            field: entry.field,
            message: `User with ID ${entry.id} does not exist`,
        }));
};

const validateUserGroups = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
): RowError[] => {
    const userGroupFields: Array<{ id: number | undefined; field: string }> = [
        { id: row.Responsible_Group_ID, field: "Responsible_Group_ID" },
        { id: row.Reviewer_Group_ID, field: "Reviewer_Group_ID" },
        { id: row.SecondReviewer_Group_ID, field: "SecondReviewer_Group_ID" },
    ];

    if (row.Default_Claimant_ID && row.GroupAssignment_Type === "Group") {
        const groupUsers = context.existingUsersWithinGroupIds.get(
            row.Responsible_Group_ID!
        );

        if (!groupUsers || !groupUsers.has(row.Default_Claimant_ID)) {
            return [
                {
                    row: index + FIRST_DATA_ROW,
                    field: "Default_Claimant_ID",
                    message: `User with ID ${row.Default_Claimant_ID} is not a member of the Group`,
                },
            ];
        }
    } else {
        userGroupFields.push({
            id: row.Default_Claimant_ID,
            field: "Default_Claimant_ID",
        });
    }

    return userGroupFields
        .filter((entry) => entry.id !== undefined)
        .filter((entry) => !context.existingUserGroupIds.has(entry.id!))
        .map((entry) => ({
            row: index + FIRST_DATA_ROW,
            field: entry.field,
            message: `User group with ID ${entry.id} does not exist`,
        }));
};

const validateEntity = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
): RowError[] => {
    if (row.Entity_ID === undefined) {
        return [];
    }

    const controlProgramId = context.controlProgramIdByControlId.get(
        row.Control_ID
    );
    if (controlProgramId === undefined) {
        // The control does not exist; already reported by validateControl
        return [];
    }

    const linkedEntityIds =
        context.entityIdsByControlProgramId.get(controlProgramId);
    if (!linkedEntityIds?.has(row.Entity_ID)) {
        return [
            {
                row: index + FIRST_DATA_ROW,
                field: "Entity_ID",
                message: `Entity with ID ${row.Entity_ID} does not exist or is not linked to the control program of control ${row.Control_ID}`,
            },
        ];
    }

    return [];
};

const validateTags = (
    row: AssignmentRow,
    index: number,
    context: ValidationContext
): RowError[] => {
    if (!row.tagColumns) {
        return [];
    }

    return Object.entries(row.tagColumns).flatMap(([category, tagNames]) => {
        const categoryData = context.tagsData.tagCategories.find(
            (c) => c.name.toLowerCase() === category.toLowerCase() && c.active
        );

        if (!categoryData) {
            return [
                {
                    row: index + FIRST_DATA_ROW,
                    field: `AssigneeTags: ${category}`,
                    message: `Tag category "${category}" does not exist`,
                },
            ];
        }

        return tagNames
            .filter(
                (tagName) =>
                    !context.tagsData.tags.some(
                        (t) =>
                            t.tagCategoryId === categoryData.id &&
                            t.name.toLowerCase() === tagName.toLowerCase() &&
                            t.active
                    )
            )
            .map((tagName) => ({
                row: index + FIRST_DATA_ROW,
                field: `AssigneeTags: ${category}`,
                message: `Tag "${tagName}" does not exist in category "${category}"`,
            }));
    });
};

export function validateControlAssignments(
    rows: AssignmentRow[],
    context: ValidationContext
): OperationResult<AssignmentRow[]> {
    const validators: Validator[] = [
        validateControl,
        validateUsers,
        validateUserGroups,
        validateEntity,
        validateTags,
    ];

    const errors = rows.reduce<RowError[]>((acc, row, index) => {
        const rowErrors = validators.flatMap((validator) =>
            validator(row, index, context)
        );
        return [...acc, ...rowErrors];
    }, []);

    if (errors.length !== 0) {
        return {
            success: false,
            errors: errors.map(
                (err) => `Row ${err.row}: ${err.field} - ${err.message}`
            ),
        };
    }

    return {
        success: true,
        data: rows,
    };
}
